import React, { createContext, useCallback, useContext, useMemo } from "react";

import { UseMutateFunction } from "@tanstack/react-query";

import { useSsoTokenRequestMutation } from "@/api/auth/useAuthAPI";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import {
  CheckProviderResponse,
  ProviderScope,
  SsoTokenRequestPayload,
  SsoTokenRequestResponse
} from "@/types/auth";
import { CustomError } from "@/utils/errors";
import { createPkceParameters } from "@/utils/pkceUtils";

type BuildAuthPayloadType = {
  authority: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  codeChallenge: string;
  state: string;
  scopes: ProviderScope[];
};

const buildAuthUrl = ({
  authority,
  clientId,
  redirectUri,
  responseType,
  codeChallenge,
  scopes,
  state
}: BuildAuthPayloadType) => {
  const params = new URLSearchParams({
    client_id: clientId,
    scope: scopes.join(" "),
    redirect_uri: redirectUri,
    response_type: responseType,
    prompt: "select_account",
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state
  });

  return `${authority}?${params.toString()}`;
};
interface SsoContextValue {
  initSso: (response: CheckProviderResponse) => Promise<void>;
  ssoTokenRequest: UseMutateFunction<
    SsoTokenRequestResponse,
    { message: CustomError },
    { requestUrl: string; payload: SsoTokenRequestPayload },
    unknown
  >;
}

const SsoContext = createContext<SsoContextValue | null>(null);

export const SsoProvider = ({ children }: { children: React.ReactNode }) => {
  const { mutate: ssoTokenRequest } = useSsoTokenRequestMutation();

  const [_, setSavedProviderConfig] = useLocalStorage("providerConfig", "");

  const initSso = useCallback(
    async (response: CheckProviderResponse) => {
      // Generate PKCE parameters
      const { code_challenge: codeChallenge, state } =
        await createPkceParameters();

      // Save provider config to local storage
      setSavedProviderConfig(JSON.stringify(response));

      const providerUrl = buildAuthUrl({
        authority: response.auth_service_url,
        clientId: response.client_id,
        scopes: response.scopes,
        redirectUri: response.redirect_uri,
        responseType: response.response_type,
        codeChallenge,
        state
      });

      // Redirect to provider auth page
      globalThis.location.href = providerUrl.toString();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const value = useMemo(
    () => ({ initSso, ssoTokenRequest }),
    [initSso, ssoTokenRequest]
  );

  return <SsoContext.Provider value={value}>{children}</SsoContext.Provider>;
};

export const useSso = () => {
  const context = useContext(SsoContext);
  if (!context) {
    throw new Error("useSso must be used within a SsoProvider");
  }
  return context;
};
